/**
 * LeatherSliceTool — the leather "Slice" method (#1001, epic #781): drag the
 * knife across a part to cut it into two parts.
 *
 * A thin shell, like `LeatherDeleteTool` / `LeatherMirrorTool` /
 * `LeatherRoundTool`: it OWNS a `LeatherSliceController` and drives it by
 * composition, supplying only the editor plumbing. The gesture, the live cut
 * line and the commit live in the controller.
 *
 * Mode-independent, like Mirror / Round / Delete — you cut a part, not a mode —
 * so `app.ts` hides the Leather / Cutout / Stitch row while it's active.
 * Slicing a cutout or a seam is out of scope (#1001): two cutouts already
 * express a cut cutout, and Edit-points covers splitting a seam.
 */
import type paper from "paper";
import { ToolBase } from "./ToolBase";
import type { PointerSample } from "../canvas/PointerInput";
import type { Scene, SceneSelection } from "../canvas/Scene";
import type { Project } from "../model/Project";
import type { EditorContext } from "./EditorContext";
import { setCanvasCursor } from "../canvas/canvasCursor";
import {
  LeatherSliceController,
  type LeatherSliceHost,
} from "./leatherSliceController";

export class LeatherSliceTool extends ToolBase implements LeatherSliceHost {
  id = "leatherSlice";
  label = "Slice";
  // Crosshair: the press position is the start of a cut LINE, so what matters
  // is exactly where the knife goes in.
  cursor = "crosshair";

  private slice = new LeatherSliceController(this);

  // --- LeatherSliceHost ----------------------------------------------------

  getScope(): paper.PaperScope {
    return this.ctx.scene.getPaperScope();
  }

  getScene(): Scene {
    return this.ctx.scene;
  }

  getProject(): Project {
    return this.ctx.history.state;
  }

  commit(label: string, mutate: (proj: Project) => Project): void {
    this.ctx.commit(label, mutate);
  }

  setSelection(sel: SceneSelection): void {
    this.ctx.setSelection(sel);
  }

  getContext(): EditorContext {
    return this.ctx;
  }

  // --- Pointer flow --------------------------------------------------------

  onActivate(): void {
    setCanvasCursor(this.getScope(), this.cursor);
  }

  onDeactivate(): void {
    this.slice.clear();
  }

  onDown(s: PointerSample, p: { x: number; y: number }): void {
    this.slice.handleDown(p, s);
  }

  onMove(s: PointerSample, p: { x: number; y: number }): void {
    this.slice.handleMove(p, s);
  }

  onUp(): void {
    this.slice.handleUp();
  }

  /** A mid-drag Shift / Ctrl / Cmd press re-resolves the live cut (#1013), so
   *  the straight-vs-freehand switch happens under the user's finger rather
   *  than on the next pointer move. */
  override onModifiersChanged(m: {
    shiftKey: boolean;
    ctrlKey: boolean;
    metaKey: boolean;
    altKey: boolean;
  }): void {
    this.slice.handleModifiers(m);
  }

  onCancel(): void {
    this.slice.clear();
  }

  /** Test hook (#1001): how many samples the live cut stroke holds, or 0 when
   *  no cut is in flight. */
  getStrokeLength(): number {
    return this.slice.getStrokeLength();
  }

  /** Test hook (#1013): whether the live cut is straight (angle-snapped). */
  isStraightCut(): boolean {
    return this.slice.isStraightCut();
  }
}
